import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';

export type AccessLevel = 'all' | 'university' | 'college' | 'subject';

/** Immutable snapshot of what the logged-in user is allowed to see. */
export interface AccessScope {
  readonly level: AccessLevel;
  readonly is_all: boolean;
  readonly university_id: number | null;
  readonly college_id: number | null;
  readonly subject_ids: readonly number[];
  /** Which filter controls must render disabled because their value is fixed. */
  readonly locked: {
    readonly university: boolean;
    readonly college: boolean;
    readonly subject: boolean;
  };
}

/** Shared singleton for unrestricted users — avoids allocating an identical object per login. */
export const UNRESTRICTED_ACCESS_SCOPE: AccessScope = Object.freeze({
  level: 'all',
  is_all: true,
  university_id: null,
  college_id: null,
  subject_ids: Object.freeze([]) as readonly number[],
  locked: Object.freeze({ university: false, college: false, subject: false }),
});

/** Positive integer id, or null when absent/invalid. */
const to_id = (value: any): number | null => {
  const id = Number(value);
  return Number.isInteger(id) && id > 0 ? id : null;
};

/**
 * Derive the access scope from a user row + their role. Pure — no service state — so it
 * is trivially testable and safe to call from anywhere.
 *
 * Note: `belongs_to` lives on the Role, never on the user row.
 */
export function build_access_scope(user_data: any, user_role_data: any): AccessScope {
  const raw = String(user_role_data?.belongs_to ?? '').trim().toLowerCase();
  if (raw !== 'university' && raw !== 'college' && raw !== 'subject') {
    return UNRESTRICTED_ACCESS_SCOPE;
  }
  const level: AccessLevel = raw;

  // Reached only for university / college / subject — 'all' returned above.
  const university_id = to_id(user_data?.university_id);
  const college_id = to_id(user_data?.college_id);
  const subject_ids: readonly number[] = level === 'subject' && Array.isArray(user_data?.subject_ids)
    ? Object.freeze(user_data.subject_ids.map(to_id).filter((id: number | null): id is number => id !== null))
    : Object.freeze([]);

  return Object.freeze({
    level,
    is_all: false,
    university_id,
    college_id: level === 'college' || level === 'subject' ? college_id : null,
    subject_ids,
    locked: Object.freeze({
      university: university_id !== null,
      college: (level === 'college' || level === 'subject') && college_id !== null,
      // Locked only when there is exactly one choice; several subjects still need a picker.
      subject: subject_ids.length === 1,
    }),
  });
}

@Injectable({
  providedIn: 'root'
})
export class GlobalService {
  // Only the evaluation page still uses this (full-screen mode while scoring a script) — the
  // manual header toggle was removed so the sidebar can't otherwise be hidden/shown.
  sidebar: boolean = false
  user_data: any = {}
  user_role_data: any = {}
  university_settings: any = {}
  users: any = {
    role: [],
    permission: [],
  }
  constructor(public toastr: ToastrService) {
    this.check_and_fetch_from_local_storage()
    console.log(this.users);

  }

  check_and_fetch_from_local_storage() {
    let app_user = localStorage.getItem('app_user')
    let user_role = localStorage.getItem('user_role')
    let university_settings = localStorage.getItem('university_settings')
    if (app_user) {
      this.user_data = JSON.parse(app_user);
    }
    if (user_role) {
      this.user_role_data = JSON.parse(user_role);
    }
    if (university_settings) {
      this.university_settings = JSON.parse(university_settings);
    }
    this.refresh_access_scope();
  }
  // ---------------------------------------------------------------------------
  // Access Level (Role.belongs_to): 'all' | 'university' | 'college' | 'subject'
  //
  // The backend hard-enforces this on every list API, so this exists only to keep the
  // UI honest — pre-select and lock the filters the user cannot change, so they aren't
  // offered choices that would return nothing.
  //
  // Computed ONCE per login and cached in `access_scope`. Templates bind to its plain
  // properties (`gs.access_scope.locked.university`), which is a bare property read on
  // every change-detection pass — no string parsing, no array allocation, and a stable
  // object identity so bindings never report a false change.
  // ---------------------------------------------------------------------------

  access_scope: AccessScope = UNRESTRICTED_ACCESS_SCOPE;

  /**
   * Recompute the cached scope from the current user/role data.
   * Call after anything that replaces user_data or user_role_data.
   */
  refresh_access_scope(): void {
    this.access_scope = build_access_scope(this.user_data, this.user_role_data);
  }

  /**
   * Pre-fill a list page's filter params with the values the access level forces.
   * Call before the first get_list() so the UI shows the same rows the API returns.
   */
  apply_access_scope_to_params(params: Record<string, any>): Record<string, any> {
    if (!params) return params;
    const { university_id, college_id, subject_ids } = this.access_scope;

    if (university_id !== null) params['university_id'] = university_id;
    if (college_id !== null) params['college_id'] = college_id;
    // Only a single assigned subject can be forced onto a one-value filter; with several,
    // the user still picks — the backend restricts the result set either way.
    if (subject_ids.length === 1) params['subject_id'] = subject_ids[0];

    return params;
  }

  toastr_shows_function(
    message: string,
    title: string,
    type: 'success' | 'error' | 'info' | 'warning' | 'delete'
  ) {
    const toastr_config = {
      timeOut: 5000,
      progressBar: false,
      closeButton: true,
      toastClass: `toast-base custom-${type}-toast`,
      positionClass: 'toast-top-right',
      enableHtml: true,
    };
    switch (type.toLowerCase()) {
      case 'success':
        this.toastr.success(message, `${title}:`, toastr_config);
        break;
      case 'error':
        this.toastr.error(message, `${title}:`, toastr_config);
        break;
      case 'info':
        this.toastr.info(message, `${title}:`, toastr_config);
        break;
      case 'warning':
        this.toastr.warning(message, `${title}:`, toastr_config);
        break;
      case 'delete':
        this.toastr.error(message, `${title}:`, toastr_config);
        break;
      default:
        console.error(`Unknown toastr type: ${type}`);
        break;
        this.toastr.error('खूप मोठा error message आहे जो 2-3 ओळींपर्यंत जाईल.', 'चूक', {
          closeButton: true,
          toastClass: 'toast-base custom-error-toast',
        });
        this.toastr.error('This is a very long error message that will span multiple lines and should be handled properly by the toastr service.', 'Error', {
          closeButton: true,
          toastClass: 'toast-base custom-error-toast',
        });
    }
  }

  start_end_date_formate_return(form: any, date_range: any, start_control_name: string, end_control_name: string, isRequired: boolean = false,) {
    const startControl = form.get(start_control_name);
    const endControl = form.get(end_control_name);
    if (date_range && date_range.start && date_range.end) {
      const form_object: any = {};
      form_object[start_control_name] = date_range.start;
      form_object[end_control_name] = date_range.end;
      form.patchValue(form_object);
    }
    if (isRequired) {
      startControl?.setValidators(Validators.required);
      endControl?.setValidators(Validators.required);

      startControl?.updateValueAndValidity();
      endControl?.updateValueAndValidity();
    }
  }

  normalizeBulkEntry(entry: any): any {
    const parsedEntry: any = {};

    const truthyValues = ['true', 'yes', 'active'];
    const falsyValues = ['false', 'no', 'inactive'];

    const forceStringFields = [
      'udise_code',
      'landline_number',
      'hos_mobile',
      'mobile_phone_used_for_teaching',
      'barcode'
    ];

    Object.keys(entry).forEach(key => {
      const raw = entry[key];

      // Force these fields to STRING only
      if (forceStringFields.includes(key)) {
        parsedEntry[key] = raw?.toString();
        return;
      }

       if (typeof raw === 'number') {
    parsedEntry[key] = raw;
    return;
  }

      if (typeof raw === 'string') {
        const trimmed = raw.trim();
        const value = trimmed.toLowerCase();

        if (/^\[\s*'.*'\s*\]$/.test(trimmed)) {
          try {
            parsedEntry[key] = JSON.parse(trimmed.replace(/'/g, '"'));
          } catch {
            parsedEntry[key] = raw;
          }
        }

        else if (truthyValues.includes(value)) {
          parsedEntry[key] = true;
        } else if (falsyValues.includes(value)) {
          parsedEntry[key] = false;
        }

        else if (value === 'null') {
          parsedEntry[key] = null;
        } else if (value === 'undefined') {
          parsedEntry[key] = undefined;
        }

        // 👉 This will NOT run for forced-string fields now
        else if (key !== 'contact' &&  /^-?\d+(\.\d+)?$/.test(trimmed)) {
          const numberValue = parseFloat(trimmed);
          parsedEntry[key] = Number.isInteger(numberValue) ? Math.floor(numberValue) : numberValue;
        }

        else {
          parsedEntry[key] = raw;
        }
      } else {
        parsedEntry[key] = raw;
      }
    });
    return parsedEntry;
  }

  async bulk_form_submission(body: any, service: any, params: any, get_list: Function, bulk_modal?: any, spinner?: any) {
    try {
      body = body.map((entry: any) => this.normalizeBulkEntry(entry));
      let response = await service.add({ bulk_upload: body });
      await get_list(params);
      bulk_modal?.close();
      spinner?.stopLoader('add-loader');
      this.toastr_shows_function(response.message, 'Successful', 'success')
    } catch (error: any) {
      console.error(error?.error?.message, '');
      this.toastr_shows_function(error?.error?.message, 'Error', 'error')
    }

  }

  go_back() {
    window.history.back();
  }

  addHierarchyIds(obj: any) {
    const result = { ...obj };

    function traverse(node: any) {
      if (!node || typeof node !== "object") return;

      for (const key in node) {
        const value = node[key];
        if (
          value &&
          typeof value === "object" &&
          "id" in value &&
          "name" in value
        ) {
          result[`${key.toLowerCase()}_id`] = value.id;
          traverse(value);
        }
      }
    }

    traverse(obj);
    return result;
  }

  dropdownRegistry: { [depended: number]: any } = {};

  registerDropdown(event: { depended: number; ref: any }) {
    this.dropdownRegistry[event.depended] = event.ref;
  }

  onDropdownChange(event: { value: any, depended: number }) {
    console.log("Dropdown changed:", event);

    const current = event.depended;
    for (const key of Object.keys(this.dropdownRegistry).map(Number)) {
      if (key > current) {
        this.dropdownRegistry[key]?.reset();
      }
    }
  }

}
